import React, { useState, useEffect } from 'react'
import { Scale, Check, X, ChevronDown, Save, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

const API_URL = process.env.REACT_APP_BACKEND_URL

const MOTIFS_REFUS = [
  { value: 'tiers_non_cooperatif', label: 'Tiers non cooperatif' },
  { value: 'dommage_trop_couteux', label: 'Dommage trop couteux' },
  { value: 'corporel', label: 'Corporel' },
  { value: 'non_propose_depot', label: 'Non proposé par le dépôt' },
  { value: 'non_eligible', label: 'Non éligible' },
]

export default function EligibiliteAmiableBlock({ sinistre, onRefresh, embedded = false }) {
  const [regleAmiable, setRegleAmiable] = useState(sinistre?.amiable_propose_tiers ?? null)
  const [motif, setMotif] = useState(sinistre?.amiable_motif_refus || '')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const val = sinistre?.amiable_propose_tiers
    setRegleAmiable(val === true ? true : val === false ? false : null)
    setMotif(sinistre?.amiable_motif_refus || '')
  }, [sinistre?.id, sinistre?.amiable_propose_tiers, sinistre?.amiable_motif_refus])

  // Motif historique hors liste (ex-"Autre (preciser)") : conserve en option pour ne rien perdre
  const motifHorsListe = motif && !MOTIFS_REFUS.find(mr => mr.value === motif) ? motif : null

  async function handleSave() {
    setSaving(true)
    try {
      const r = await fetch(`${API_URL}/api/sinistres/${sinistre.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amiable_propose_tiers: regleAmiable,
          amiable_motif_refus: regleAmiable === false ? motif : null,
        }),
      })
      if (!r.ok) throw new Error('Erreur serveur')
      toast.success('Eligibilite amiable mise a jour')
      onRefresh()
    } catch (e) {
      toast.error(e.message)
    } finally {
      setSaving(false)
    }
  }

  const isChanged = regleAmiable !== (sinistre?.amiable_propose_tiers ?? null) ||
    (regleAmiable === false && motif !== (sinistre?.amiable_motif_refus || ''))

  return (
    <div className={embedded ? '' : 'bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden'} data-testid="eligibilite-amiable-block">
      {embedded ? (
        <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wide mb-2">Reglement amiable</p>
      ) : (
      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
        <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
          <Scale size={16} className="text-indigo-500" />
          Reglement amiable
        </h2>
      </div>
      )}
      <div className={embedded ? 'space-y-4' : 'p-5 space-y-4'}>
        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">Le sinistre a-t-il ete regle en amiable ?</p>
          <div className="flex gap-2">
            <button
              onClick={() => setRegleAmiable(true)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                regleAmiable === true
                  ? 'bg-emerald-100 border-emerald-400 text-emerald-800 ring-1 ring-emerald-300'
                  : 'bg-white border-slate-200 text-slate-500 hover:border-emerald-300'
              }`}
              data-testid="amiable-oui-btn"
            >
              <Check size={14} /> Oui
            </button>
            <button
              onClick={() => setRegleAmiable(false)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                regleAmiable === false
                  ? 'bg-red-100 border-red-400 text-red-800 ring-1 ring-red-300'
                  : 'bg-white border-slate-200 text-slate-500 hover:border-red-300'
              }`}
              data-testid="amiable-non-btn"
            >
              <X size={14} /> Non
            </button>
          </div>
        </div>

        {regleAmiable === false && (
          <div className="space-y-2" data-testid="motif-refus-section">
            <p className="text-xs font-medium text-slate-600">Motif</p>
            <div className="relative">
              <select
                value={motif}
                onChange={e => setMotif(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white appearance-none pr-8 focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400"
                data-testid="motif-refus-select"
              >
                <option value="">Selectionner un motif</option>
                {MOTIFS_REFUS.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
                {motifHorsListe && <option value={motifHorsListe}>{motifHorsListe}</option>}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          </div>
        )}

        {isChanged && (
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving || (regleAmiable === false && !motif)}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              data-testid="save-eligibilite-btn"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Enregistrer
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
